import React, { useEffect } from "react";
import _ from "lodash";

import { CustomerSucessController } from "@talentbait/shared-components";

import {
  Wrapper,
  Label,
  SelectStyled,
  AlternativePlaceholder,
  SelectContainer
} from "./DropDownFilter.styled";

import DashboardActions from "../DashboardActions";
import { ALL_STATUSES } from "../DashboardStore";
import logger from "../../../utils/LoggerFrontend";
import { useState } from "react";
import i18next from "i18next";

const createHandleFilter =
  ({ history, currentFilters }) =>
  ({ statuses: newStatuses, companies: newCompanies, owners: newOwners, dates: newDates }) => {
    const statuses = newStatuses || currentFilters.statuses;
    const companies = newCompanies || currentFilters.companies;
    const owners = newOwners || currentFilters.owners;
    const dates = newDates || currentFilters.dates;

    const newFilters = { statuses, companies, owners, dates };

    const serializedFilters = newFilters
      ? `?` +
        Object.keys(newFilters)
          .map((key) => key + "=" + newFilters[key])
          .join("&")
      : "";

    DashboardActions.handleFilters({ statuses, companies, owners, dates });

    history.push({
      search: serializedFilters
    });

    return newFilters;
  };

const getFilterOptions = ({ filterOptions, filterKeyName, defaultOptionLabel }) => {
  const newFilterOptions = [...filterOptions];

  if (defaultOptionLabel) {
    newFilterOptions.unshift({
      value: "DEFAULT",
      label: defaultOptionLabel
    });
  }

  newFilterOptions.unshift({
    value: "NONE",
    label: `${i18next.t("All")} ${filterKeyName}`
  });

  return newFilterOptions;
};

const getFilterValues = ({ filterOptions, filters, filterKey }) =>
  _.filter(filterOptions, ({ value }) => filters[filterKey]?.includes(value));

const onChangeFilter =
  ({
    filterOptions,
    handleFilter,
    userProfile,
    finishedCampaignsHaveBeenLoaded,
    debouncedFetchCampaigns,
    filterKey,
    switchPlaceholder,
    defaultOptions,
    setPlaceholderIsForcedToShown
  }) =>
  (selectedOptions) => {
    setPlaceholderIsForcedToShown(false);

    const { organizationId } = userProfile;
    let optionNames = selectedOptions.map(({ value }) => value);
    let switchToDefaultOptionLabel = false;

    const selectedOption = optionNames[optionNames.length - 1];

    const allOptionWasSelected = selectedOption === "ALL";
    const noOptionWasSelected = selectedOption === "NONE";
    const defaultWasSelected = selectedOption === "DEFAULT";

    if (allOptionWasSelected) {
      optionNames = filterOptions.map(({ value }) => value);
    } else if (noOptionWasSelected) {
      optionNames = [];
    } else if (defaultWasSelected) {
      switchToDefaultOptionLabel = true;
      optionNames = defaultOptions;
    }

    switchPlaceholder({ switchToDefaultOptionLabel });

    const newFilter = handleFilter({ [filterKey]: optionNames });

    CustomerSucessController.send(`Dashboard:handleFilter:${filterKey}`);
    logger.log("DropDownFilter:onChangeFilter", "filter changed", {
      filterKey,
      selectedOption,
      newFilter
    });

    if (noOptionWasSelected && filterKey === "statuses") {
      debouncedFetchCampaigns({
        statuses: ALL_STATUSES,
        organizationId,
        finishedCampaignsHaveBeenLoaded
      });
    } else {
      debouncedFetchCampaigns({ ...newFilter, organizationId, finishedCampaignsHaveBeenLoaded });
    }
  };

function DropDownFilter({
  placeholder,
  history,
  filters,
  filterOptions,
  userProfile,
  finishedCampaignsHaveBeenLoaded,
  isLoadingCampaigns,
  filterKey,
  filterKeyName,
  debouncedFetchCampaigns,
  label,
  defaultOptionLabel,
  defaultOptions
}) {
  const handleFilter = createHandleFilter({ history, currentFilters: filters });
  const [dynamicPlaceholder, setDynamicPlaceholder] = useState(placeholder);
  const [placeholderIsForcedToShown, setPlaceholderIsForcedToShown] = useState(false);

  const switchPlaceholder = ({ switchToDefaultOptionLabel = false }) => {
    if (switchToDefaultOptionLabel) setPlaceholderIsForcedToShown(true);
    else setPlaceholderIsForcedToShown(false);
    setDynamicPlaceholder(switchToDefaultOptionLabel ? defaultOptionLabel : placeholder);
  };

  useEffect(() => {
    const currentSelectedOptions = filters[filterKey];

    const compareOptions = (value) => defaultOptions.includes(value);

    if (defaultOptions?.length && currentSelectedOptions.every(compareOptions)) {
      switchPlaceholder({ switchToDefaultOptionLabel: true });
    }
  }, []);

  return (
    <Wrapper>
      <Label>{label}</Label>
      <SelectContainer>
        {" "}
        <SelectStyled
          isDisabled={isLoadingCampaigns}
          placeholderIsForcedToShown={placeholderIsForcedToShown}
          isSearchable
          isMulti
          isClearable={false}
          backspaceRemovesValue={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          placeholder={dynamicPlaceholder}
          options={getFilterOptions({
            filterOptions,
            filters,
            filterKey,
            filterKeyName,
            defaultOptionLabel
          })}
          value={getFilterValues({ filterOptions, filters, filterKey })}
          onChange={onChangeFilter({
            filterOptions,
            handleFilter,
            userProfile,
            finishedCampaignsHaveBeenLoaded,
            debouncedFetchCampaigns,
            filterKey,
            defaultOptionLabel,
            switchPlaceholder,
            defaultOptions,
            setPlaceholderIsForcedToShown
          })}
          onKeyDown={() => {
            if (placeholderIsForcedToShown && defaultOptionLabel) {
              switchPlaceholder({});
            }
          }}
        />
        {placeholderIsForcedToShown && defaultOptionLabel && (
          <AlternativePlaceholder>{dynamicPlaceholder}</AlternativePlaceholder>
        )}
      </SelectContainer>
    </Wrapper>
  );
}

export default DropDownFilter;
